import Modal from './Modal';
import React, { FC, useEffect, useState } from 'react';
import classes from './BuyDialog.module.scss';
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import Alert from './Alert';
import { ClosableCompProps } from './types';
import Button from './Button';
import { getUserInfo } from '../service/userService';
import axiosClient from '../service/axiosClient';
/**
 * Paypal buy dialog. Displays two Paypal buttons.
 */

const creditArr = [
  { credits: 10, value: 1.2 },
  { credits: 50, value: 4.5 },
  { credits: 100, value: 9 },
  { credits: 200, value: 18 }
];

const BuyDialogPaypal: FC<ClosableCompProps> = ({ onClose }) => {
  const [credits, setCredits] = useState<number>(10);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);

  const handleClose = async () => {
    getUserInfo().then(onClose);
  };

  const handleBuyResult = (success: boolean) => {
    if (success) setShowAlert(true);
    else setShowErrorAlert(true);
  };

  const handleChange = (cr: number) => {
    setCredits(cr);
  };

  return (
    <>
      {showAlert && (
        <Alert
          title="Successful purchase"
          message="Thank you for buying credits! Your credits will appear shortly - please reload the page!"
          onClose={() => {
            setShowAlert(false);
            handleClose();
          }}
        />
      )}
      {showErrorAlert && (
        <Alert
          title="Purchase error"
          message="Purchase failed!"
          onClose={() => {
            setShowErrorAlert(false);
            handleClose();
          }}
        />
      )}
      <Modal>
        <div className={classes.mainCont}>
          <div className={classes.bg}></div>
          <div className={classes.panel}>
            <div className={classes.header}>
              <div className={classes.title}>Buy credits with Paypal</div>
            </div>
            <div className={classes.body}>
              <p className={classes.text}>
                Please select the amount of credits you wish to buy and click one of the buttons to check out with
                Paypal or bank card.
              </p>
              <div className={classes.optionRow}>
                <label htmlFor="amount">Amount</label>
                <select
                  name="amount"
                  id="amount"
                  value={credits}
                  onChange={(e) => handleChange(Number(e.target.value))}>
                  {creditArr.map((item) => {
                    return (
                      <option key={item.credits} value={item.credits}>
                        {`${item.credits} credits $${item.value} USD`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className={classes.optionRow}></div>

              <PayPalScriptProvider
                options={{
                  'client-id': window.runtimeConfig.PAYPAL_CLIENT_ID,
                  components: 'buttons',
                  currency: 'USD'
                }}>
                <ButtonWrapper
                  currency={'USD'}
                  showSpinner={false}
                  credits={credits}
                  onClose={handleBuyResult}
                />
              </PayPalScriptProvider>
            </div>
            <div className={classes.btnBar}>
              <Button onClick={handleClose}>Cancel</Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const style = { layout: 'vertical' as const };

// Custom component to wrap the PayPalButtons and handle currency changes
const ButtonWrapper = ({
  currency,
  showSpinner,
  credits,
  onClose
}: {
  currency: string;
  showSpinner: boolean;
  credits: number;
  onClose: (success: boolean) => void;
}) => {
  // usePayPalScriptReducer can be use only inside children of PayPalScriptProviders
  // This is the main reason to wrap the PayPalButtons in a new component
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();

  useEffect(() => {
    dispatch({
      type: 'resetOptions',
      value: {
        ...options,
        currency
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, showSpinner]);

  return (
    <>
      {showSpinner && isPending && <div className="spinner" />}
      <PayPalButtons
        style={style}
        disabled={false}
        forceReRender={[credits, currency, style]}
        fundingSource={undefined}
        createOrder={() => {
          // order (price + which userId to credit) is created server-side from the authenticated
          // session, not built from client-supplied values - see paypalCreateOrder in
          // payment.controller.ts. Previously the whole purchase_units object, including price
          // and custom_id (the userId to credit), was built here and sent to Paypal as-is, so a
          // tampered client could buy credits for $0.01 or credit someone else's account.
          return axiosClient
            .post<{ id: string }>('payment/paypalCreateOrder', { credits })
            .then((response) => response.data.id);
        }}
        onApprove={function (data, actions) {
          return actions.order
            ? actions.order.capture().then(function (data) {
                console.log('Paypal buy success', data);
                onClose(true);
              })
            : Promise.resolve();
        }}
        onError={function (error) {
          console.error('Paypal buy error', error);
          onClose(false);
        }}
      />
    </>
  );
};

export default BuyDialogPaypal;
